import React, { useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import Particles from 'react-particles';
import { loadSlim } from 'tsparticles-slim';
import { FiArrowRight } from 'react-icons/fi';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 0 150px;
  
  @media (max-width: 768px) {
    padding: 0 25px;
    min-height: 80vh;
  }
`;

const HeroContent = styled(motion.div)`
  z-index: 5;
  max-width: 1000px;
`;

const SmallHeading = styled(motion.h3)`
  font-family: var(--font-mono);
  font-size: 16px;
  color: var(--accent-primary);
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const MainHeading = styled(motion.h1)`
  font-size: 80px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 40px;
  }
`;

const SecondaryHeading = styled(motion.h2)`
  font-size: 60px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 30px;
  display: flex;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const TypedText = styled.div`
  color: var(--accent-secondary);
  padding-left: 10px;
`;

const Description = styled(motion.p)`
  font-size: 18px;
  color: var(--text-secondary);
  max-width: 600px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
  
  a {
    color: var(--accent-primary);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 1px;
      bottom: 0;
      left: 0;
      background-color: var(--accent-primary);
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.3s ease-out;
    }
    
    &:hover::after {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
`;

const CTAButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  padding: 15px 30px;
  background-color: transparent;
  border: 1px solid var(--accent-primary);
  color: var(--accent-primary);
  border-radius: 4px;
  font-family: var(--font-mono);
  font-size: 16px;
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    background-color: rgba(100, 255, 218, 0.1);
    transition: width 0.3s ease;
    z-index: -1;
  }
  
  &:hover::before {
    width: 100%;
  }
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
  
  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 14px;
  }
`;

const ParticlesContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
  pointer-events: none;
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.2,
      duration: 0.5
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const Hero = () => {
  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  const particlesConfig = {
    particles: {
      number: {
        value: 40,
        density: {
          enable: true,
          value_area: 800
        }
      },
      color: {
        value: "#64ffda"
      },
      shape: {
        type: ["circle", "triangle", "polygon"],
        polygon: {
          sides: 6
        }
      },
      opacity: {
        value: 0.3,
        random: true,
        anim: {
          enable: true,
          speed: 0.5,
          opacity_min: 0.1,
          sync: false
        }
      },
      size: {
        value: 3,
        random: true,
        anim: {
          enable: true,
          speed: 2,
          size_min: 0.1,
          sync: false
        }
      },
      line_linked: {
        enable: true,
        distance: 150,
        color: "#64ffda",
        opacity: 0.2,
        width: 1
      },
      move: {
        enable: true,
        speed: 1,
        direction: "none",
        random: true,
        straight: false,
        out_mode: "out",
        bounce: false,
        attract: {
          enable: false,
          rotateX: 600,
          rotateY: 1200
        }
      }
    },
    interactivity: {
      detect_on: "window",
      events: {
        onHover: {
          enable: false,
          mode: "grab"
        },
        onClick: {
          enable: false,
          mode: "push"
        },
        resize: true
      },
      modes: {
        grab: {
          distance: 0,
          line_linked: {
            opacity: 0
          }
        },
        push: {
          particles_nb: 0
        }
      }
    },
    retina_detect: true,
    fpsLimit: 60
  };

  return (
    <HeroSection id="home">
      <ParticlesContainer>
        <Particles
          id="tsparticles"
          init={particlesInit}
          options={particlesConfig}
          style={{ pointerEvents: 'none' }}
        />
      </ParticlesContainer>
      
      <HeroContent
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <SmallHeading variants={itemVariants}>
          Hi, my name is
        </SmallHeading>
        
        <MainHeading variants={itemVariants}>
          Nandan T.
        </MainHeading>
        
        <SecondaryHeading variants={itemVariants}>
          I'm a
          <TypedText>
            <TypeAnimation
              sequence={[
                'Security Consultant',
                2000,
                'Penetration Tester',
                2000,
                'Bug Bounty Hunter',
                2000,
                'Security Researcher',
                2000
              ]}
              wrapper="span"
              speed={50}
              repeat={Infinity}
            />
          </TypedText>
        </SecondaryHeading>
        
        <Description variants={itemVariants}>
          I specialize in vulnerability assessment, penetration testing, and secure code review. 
          Currently, I'm focused on securing applications and infrastructures at{' '}
          <a href="https://home.kpmg/in/en/home.html" target="_blank" rel="noopener noreferrer">
            KPMG India
          </a>, 
          helping organizations protect their critical assets from cyber threats.
        </Description>
        
        <CTAButton 
          href="#contact"
          variants={itemVariants}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Get In Touch <FiArrowRight />
        </CTAButton>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero; 